import { HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess, ApplicationApiClientAccess } from '@nk/access-block';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CompanyApiService } from '@nk/api-lib';
import { CacheableObject, CachingService } from '@nk/caching-lib';
import {
  AccountsAuditorNoteCompositeEntity,
  AccountsAuditorNoteEntity,
  AccountsAuditorNoteTypeEntity,
  AccountsBalanceSheetEntity,
  AccountsEntity,
  AccountsFileMetaEntity,
  AccountsHighlightsEntity,
  AccountsIncomeStatementEntity,
  AccountsStatusComposite,
  AccountsTypeEntity,
  AppFeatureKey,
  COMPANY_FLAG_FEATURES,
  COMPANY_FLAG_ROLES,
  COUNTRY_FEATURES,
  CompanyBatchEntity,
  CompanyCollectionEntity,
  CompanyCompositeEntity,
  CompanyCustomerUploadMetaEntity,
  CompanyDescriptionEntity,
  CompanyDetailsEntity,
  CompanyEmploymentEntity,
  CompanyEntity,
  CompanyEventAggregate,
  CompanyHierarchyEntity,
  CompanyHierarchyMappingEntity,
  CompanyNameEntity,
  CompanyRiskScoreOfficialPrioritizedLatestEntity,
  CompanyRoleCompositeEntity,
  CompanyRoleEntity,
  CompanyShareAggregateEntity,
  CompanyShareOwnershipCompositeEntity,
  CompanyShareOwnershipDirectEntity,
  CompanyShareOwnershipUltimateEntity,
  GeoGeometryData,
  OrganizationTypeEntity,
  PersonCompanyRoleEntity,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { Dict, NotImplemented } from '@nk/utils-lib';
import { Observable, forkJoin, from, of } from 'rxjs';
import { concatMap, first, map, mergeMap } from 'rxjs/operators';

export class ShareholderParams implements CacheableObject {
  public year: number;
  public limit: number;
  // public companyUuid: string;
  public shareIssuerCompanyIdentifier: string;
  public shareholderCompanyIdentifier: string;
  public dataSourceKey: string;
  public shareholderPersonUuid: string;
  public shareholderPersonMasterUuid: string;
  public minOwnership: number;
  public offset: number;
  private readonly _cacheString: string;

  constructor(args: {}) {
    const deprecatedList = ['companyUuid', 'shareIssuerCompanyUuid', 'shareholderCompanyUuid', 'shareholder'];
    Object.assign(this, args);
    let cacheString = '';
    Object.keys(args).map((key) => {
      if (deprecatedList.indexOf(key) >= 0) {
        console.error('key ' + key + ' is deprecated from ShareholderParams.');
      }
      cacheString += key + String(args[key]);
    });
    this._cacheString = cacheString;
  }

  public toCachingKey(): string {
    return this._cacheString;
  }
  public getShareholderHttpParams(): HttpParams {
    let httpParams = new HttpParams();
    if (this.shareholderPersonUuid) {
      httpParams = httpParams.append('shareholder_person_uuid', this.shareholderPersonUuid);
    }
    if (this.shareholderPersonMasterUuid) {
      httpParams = httpParams.append('shareholder_person_master_uuid', this.shareholderPersonMasterUuid);
    }
    // if (this.companyUuid) {
    //   httpParams = httpParams.append('company_uuid', this.companyUuid);
    // }
    if (this.dataSourceKey) {
      httpParams = httpParams.append('data_source_key', String(this.dataSourceKey));
    }
    if (this.shareIssuerCompanyIdentifier) {
      httpParams = httpParams.append('share_issuer_company_identifier', this.shareIssuerCompanyIdentifier);
    }
    if (this.shareholderCompanyIdentifier) {
      httpParams = httpParams.append('shareholder_company_identifier', this.shareholderCompanyIdentifier);
    }
    if (this.shareholderPersonMasterUuid) {
      httpParams = httpParams.append('shareholder_person_master_uuid', this.shareholderPersonMasterUuid);
    }
    if (this.minOwnership) {
      httpParams = httpParams.append('min_ownership', String(this.minOwnership));
    }
    if (this.year) {
      httpParams = httpParams.append('year', String(this.year));
    }
    if (this.offset) {
      httpParams = httpParams.append('offset', String(this.offset));
    }
    if (this.limit) {
      httpParams = httpParams.append('limit', String(this.limit));
    }
    return httpParams;
  }
}

// Only company_share_ownership_type_identifier=direct_ownership is supported for now.
type CompanyShareOwnershipTypeIdentifier = 'direct_ownership';

type OwnershipFilterKey = 'corporate_group';

@Injectable()
export class CompanyService implements CompanyApiService {
  constructor(
    private _applicationApi: ApplicationApiClient,
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
  ) {}

  public removeCompanyFromBatch(companyBatchUuid: string, companyUuid: string): Observable<CompanyEntity> {
    return this._analysisApiAccess.delete<CompanyEntity>(
      `/company-batch/${companyBatchUuid}/company/${companyUuid}`,
      {},
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public postCompanyBatchWithCompanies(
    companyList: Partial<CompanyEntity>[],
    forcePostFlag = false,
  ): Observable<CompanyBatchEntity> {
    let companyBatch: CompanyBatchEntity;
    return this._analysisApiAccess.post<CompanyBatchEntity>('/company-batch', null).pipe(
      mergeMap((cb) => {
        companyBatch = cb;
        let params = new HttpParams();
        if (forcePostFlag === true) {
          params = params.append('force_post_flag', 'true');
        } else if (forcePostFlag === false) {
          params = params.append('force_post_flag', 'false');
        }
        return this._analysisApiAccess.post<CompanyEntity>(
          `/company-batch/${companyBatch.uuid}/company`,
          companyList,
          { params: params },
          [AppFeatureKey.base, ...COUNTRY_FEATURES],
        );
      }),
      map(() => {
        return companyBatch;
      }),
    );
  }

  public companyBatchCache = this._cachingService.create(this, this.fetchCompanyBatch);
  public fetchCompanyBatch(companyBatchIdentifier: string): Observable<CompanyBatchEntity> {
    return this._analysisApiAccess.get<CompanyBatchEntity>(`/company-batch/${companyBatchIdentifier}`);
  }

  public postCompanyBatch(): Observable<CompanyBatchEntity> {
    return this._analysisApiAccess.post<CompanyBatchEntity>('/company-batch', null);
  }

  public postCompaniesToBatch(
    companyBatchUuid: string,
    companies: Partial<CompanyEntity>[],
    returnResultsFlag = true,
  ): Observable<CompanyEntity[]> {
    let params = new HttpParams();
    params = params.append('return_results_flag', returnResultsFlag ? 'true' : 'false');
    return this._analysisApiAccess.post<CompanyEntity[]>(`/company-batch/${companyBatchUuid}/company`, companies, {
      params,
    });
  }

  public companyCache = this._cachingService.create(this, this.fetchCompany);
  public fetchCompany(companyIdentifier: string): Observable<WithUuid<CompanyEntity>> {
    return this._analysisApiAccess.get<WithUuid<CompanyEntity>>(`/company/${companyIdentifier}`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public topParentCompanyCache = this._cachingService.create(this, this.fetchTopParentCompany);
  public fetchTopParentCompany(
    companyIdentifier: string,
    companyHierarchyKey = 'corporate_group',
  ): Observable<CompanyEntity> {
    return this.companyHierarchyCache.get(companyHierarchyKey).pipe(
      mergeMap((companyHierarchyList) => {
        const companyHierarchy = companyHierarchyList[0];

        let params = new HttpParams();
        params = params.append('top_parent_company_identifier', companyIdentifier);

        return forkJoin(
          this.fetchCompanyHierarchyMappingListByHierarchyAndCompany(companyHierarchy.uuid, companyIdentifier).pipe(
            first(),
          ), // you  have a top parent
          this.fetchCompanyHierarchyMappingListByCompanyHierarchy(companyHierarchy.uuid, params).pipe(first()), // you are the top parent company
        );
      }),
      mergeMap((payload) => {
        const [topParentCompanyByCompany, topParentCompanyByTopCompany] = payload;
        if (topParentCompanyByCompany.length) {
          return this.companyCache.get(topParentCompanyByCompany[0].top_parent_company_uuid);
        } else if (topParentCompanyByTopCompany.length) {
          return this.companyCache.get(topParentCompanyByTopCompany[0].top_parent_company_uuid); // supplied company is top parent company.
        } else {
          return of(null as CompanyEntity);
        }
      }),
    );
  }

  public companyHierarchyCache = this._cachingService.create(this, this.fetchCompanyHierarchy);
  public fetchCompanyHierarchy(companyHierarchyKey = 'corporate_group'): Observable<CompanyHierarchyEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_hierarchy_key', String(companyHierarchyKey));
    return this._analysisApiAccess.get<CompanyHierarchyEntity[]>('/company-hierarchy', {
      params: httpParams,
    });
  }

  public fetchCompanyHierarchyMappingListByHierarchyAndCompany(
    companyHierarchyUuid: string,
    companyUuid: string,
  ): Observable<CompanyHierarchyMappingEntity[]> {
    return this._analysisApiAccess.get<CompanyHierarchyMappingEntity[]>(
      `/company-hierarchy/${companyHierarchyUuid}/company/${companyUuid}/company-hierarchy-mapping`,
      {},
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public fetchCompanyHierarchyMappingListByCompanyHierarchy(
    companyHierarchyUuid: string,
    params: HttpParams,
  ): Observable<CompanyHierarchyMappingEntity[]> {
    return this._analysisApiAccess.get<CompanyHierarchyMappingEntity[]>(
      `/company-hierarchy/${companyHierarchyUuid}/company-hierarchy-mapping`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public fetchCompanyHierarchyMappingListByTopParent(
    companyHierarchyUuid: string,
    companyTopParentIdentifier: string,
  ): Observable<CompanyHierarchyMappingEntity[]> {
    let params = new HttpParams();
    params = params.append('top_parent_company_identifier', companyTopParentIdentifier);
    return this._analysisApiAccess.get<CompanyHierarchyMappingEntity[]>(
      `/company-hierarchy/${companyHierarchyUuid}/company-hierarchy-mapping`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyDetailsCache = this._cachingService.create(this, this.fetchCompanyDetails);
  public fetchCompanyDetails(companyUuid: string): Observable<CompanyDetailsEntity[]> {
    return this._analysisApiAccess.get<CompanyDetailsEntity[]>('/company/' + companyUuid + '/company-details', {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public companyRoleCache = this._cachingService.create(this, this.fetchCompanyRole);
  public fetchCompanyRole(): Observable<CompanyRoleEntity[]> {
    return this._analysisApiAccess.get<CompanyRoleEntity[]>('/company-role');
  }

  public companyRoleCompositeListCache = this._cachingService.create(this, this.fetchCompanyRoleCompositeList);
  public fetchCompanyRoleCompositeList(
    orgNrSchema: string,
    organizationTypeIdentifier: string,
  ): Observable<CompanyRoleCompositeEntity[]> {
    let params = new HttpParams();
    params = params.append('org_nr_schema', orgNrSchema);
    params = params.append('organization_type_identifier', organizationTypeIdentifier);
    return this._analysisApiAccess.get<CompanyRoleCompositeEntity[]>('/company-role-composite', {
      params: params,
    });
  }

  public personCompanyRoleListByCompanyCache = this._cachingService.create(
    this,
    this.fetchPersonCompanyRoleListByCompany,
  );
  public fetchPersonCompanyRoleListByCompany(
    companyUuid: string,
    currentFlag = true,
  ): Observable<PersonCompanyRoleEntity[]> {
    let params = new HttpParams();
    if (typeof currentFlag === 'boolean') {
      params = params.append('current_flag', currentFlag);
    }
    return this._analysisApiAccess.get<PersonCompanyRoleEntity[]>(
      `company/${companyUuid}/person-company-role`,
      {
        params,
      },
      [AppFeatureKey.base, AppFeatureKey.historical_roles, ...COUNTRY_FEATURES],
    );
  }

  public streamCompanyDetailsFromCompanyUuidList(companyUuidList: string[]): Observable<Dict<CompanyDetailsEntity>> {
    return from(companyUuidList).pipe(
      concatMap((companyUuid) => {
        return forkJoin([this.companyDetailsCache.get(companyUuid), of(companyUuid)]);
      }),
      mergeMap((payload) => {
        const companyDetails = payload[0];
        const companyUuid = payload[1];
        return of({
          [companyUuid]: companyDetails ? companyDetails[0] : null,
        });
      }),
    );
  }

  public companyNameListByCompanyCache = this._cachingService.create(this, this.fetchCompanyNameListByCompany);
  public fetchCompanyNameListByCompany(companyUuid: string): Observable<CompanyNameEntity[]> {
    return this._analysisApiAccess.get<CompanyNameEntity[]>(`/company/${companyUuid}/company-name`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public accountsListByCompanyCache = this._cachingService.create(this, this.fetchAccountsListByCompany);
  public fetchAccountsListByCompany(
    companyUuid: string,
    latestFlag = false,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
  ): Observable<AccountsEntity[]> {
    let params = new HttpParams();
    if (latestFlag) {
      params = params.append('latest_flag', 'true');
    }
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }

    return this._analysisApiAccess.get<AccountsEntity[]>(`/company/${companyUuid}/accounts`, { params: params }, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public accountsStatusCompositeCache = this._cachingService.create(this, this.fetchAccountsStatusComposite);
  public fetchAccountsStatusComposite(
    companyIdentifier: string,
    accountsTypeIdentifier: string,
  ): Observable<AccountsStatusComposite[]> {
    let params = new HttpParams();
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    return this._applicationApiAccess.get<AccountsStatusComposite[]>(
      `company/${companyIdentifier}/accounts-status-composite`,
      { params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public accountsHighlightsListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsHighlightsListByCompany,
  );
  public fetchAccountsHighlightsListByCompany(
    companyUuid: string,
    latestFlag = false,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
  ): Observable<AccountsHighlightsEntity[]> {
    let params = new HttpParams();
    if (latestFlag) {
      params = params.append('latest', 'true');
    }
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }
    return this._analysisApiAccess.get<AccountsHighlightsEntity[]>(
      `/company/${companyUuid}/accounts-highlights`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  // will get one and one, using the order of the companyUuid list.
  public streamLatestAccountHighlightsForCompanyUuid(
    companyUuidList: string[],
  ): Observable<Dict<AccountsHighlightsEntity>> {
    return from(companyUuidList).pipe(
      concatMap((companyUuid) => {
        const getLatest = true;
        return forkJoin([this.accountsHighlightsListByCompanyCache.get(companyUuid, getLatest), of(companyUuid)]);
      }),
      mergeMap((payload) => {
        const accountsHighlights = payload[0];
        const companyUuid = payload[1];
        return of({
          [companyUuid]: accountsHighlights ? accountsHighlights[0] : null,
        });
      }),
    );
  }

  public accountsIncomeStatementListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsIncomeStatementListByCompany,
  );
  public fetchAccountsIncomeStatementListByCompany(
    companyUuid: string,
    latestFlag = false,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
  ): Observable<AccountsIncomeStatementEntity[]> {
    let params = new HttpParams();
    if (latestFlag) {
      params = params.append('latest', 'true');
    }
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }
    return this._analysisApiAccess.get<AccountsIncomeStatementEntity[]>(
      `/company/${companyUuid}/accounts-income-statement`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public accountsBalanceSheetListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsBalanceSheetListByCompany,
  );
  public fetchAccountsBalanceSheetListByCompany(
    companyUuid: string,
    latestFlag = false,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
  ): Observable<AccountsBalanceSheetEntity[]> {
    let params = new HttpParams();
    if (latestFlag) {
      params = params.append('latest', 'true');
    }
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }
    return this._analysisApiAccess.get<AccountsBalanceSheetEntity[]>(
      `/company/${companyUuid}/accounts-balance-sheet`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public accountsAuditorNoteListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsAuditorNoteListByCompany,
  );
  public fetchAccountsAuditorNoteListByCompany(
    companyUuid: string,
    latestFlag = false,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
  ): Observable<AccountsAuditorNoteEntity[]> {
    let params = new HttpParams();
    if (latestFlag) {
      throw new NotImplemented('latest auditor notes');
      // params = params.append('latest', 'true');
    }
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }
    return this._analysisApiAccess.get<AccountsAuditorNoteEntity[]>(
      `/company/${companyUuid}/accounts-auditor-note`,
      { params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public accountsAuditorNoteCompositeListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsAuditorNoteCompositeListByCompany,
  );
  public fetchAccountsAuditorNoteCompositeListByCompany(
    companyIdentifier: string,
    accountsTypeIdentifier: string = null,
  ): Observable<AccountsAuditorNoteCompositeEntity[]> {
    let params = new HttpParams();
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }

    return this._analysisApiAccess.get<AccountsAuditorNoteCompositeEntity[]>(
      `/company/${companyIdentifier}/accounts-auditor-note-composite`,
      { params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public accountsCache = this._cachingService.create(this, this.fetchAccounts);
  public fetchAccounts(accountUuid: string): Observable<AccountsEntity> {
    return this._analysisApiAccess.get<AccountsEntity>(`/accounts/${accountUuid}`);
  }

  public accountsTypeCache = this._cachingService.create(this, this.fetchAccountsType);
  public fetchAccountsType(): Observable<AccountsTypeEntity[]> {
    return this._analysisApiAccess.get<AccountsTypeEntity[]>('/accounts-type');
  }

  public accountsAuditorNoteTypeCache = this._cachingService.create(this, this.fetchAccountsAuditorNoteType);
  public fetchAccountsAuditorNoteType(): Observable<AccountsAuditorNoteTypeEntity[]> {
    return this._analysisApiAccess.get<AccountsAuditorNoteTypeEntity[]>('/accounts-auditor-note-type');
  }

  public fetchAccountsFile(companyIdentifier: string, accountingYear: number): Observable<Blob> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_identifier', companyIdentifier);
    httpParams = httpParams.append('accounting_year', accountingYear);
    return this._applicationApi.getBlob(`/accounts-file`, { params: httpParams });
  }

  public accountsFileMetaListByCustomerCache = this._cachingService.create(
    this,
    this.fetchAccountsFileMetaListByCustomer, // TODO: this does not use cusotmer?? rename..
  );
  public fetchAccountsFileMetaListByCustomer(
    companyUuid: string,
    allowOrderingFlag?: boolean,
    forceReorderFlag?: boolean,
    accountingYears?: number[],
  ): Observable<AccountsFileMetaEntity[]> {
    let httpParams = new HttpParams();
    if (typeof allowOrderingFlag !== 'undefined') {
      httpParams = httpParams.append('allow_ordering_flag', String(allowOrderingFlag));
    }
    if (typeof forceReorderFlag !== 'undefined') {
      httpParams = httpParams.append('force_reorder_flag', String(forceReorderFlag));
    }
    if (Array.isArray(accountingYears)) {
      httpParams = httpParams.append('accounting_years', accountingYears.join(','));
    }

    return this._applicationApiAccess.get<AccountsFileMetaEntity[]>(
      `/company/${companyUuid}/accounts-file-meta`,
      { params: httpParams },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyShareAggregateListCache = this._cachingService.create(
    this,
    this.fetchCompanyShareAggregateListByCompany,
  );
  public fetchCompanyShareAggregateListByCompany(companyUuid: string): Observable<CompanyShareAggregateEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('lookback_years', String(100));
    return this._analysisApiAccess.get<CompanyShareAggregateEntity[]>(
      `/company/${companyUuid}/company-share-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  // Latest share year only (no lookback_years), so it does NOT require the
  // `historical_ownership` feature. Use this where only the latest share capital
  // is needed; `companyShareAggregateListCache` above fetches full history.
  public companyShareAggregateLatestCache = this._cachingService.create(
    this,
    this.fetchCompanyShareAggregateLatestByCompany,
  );
  public fetchCompanyShareAggregateLatestByCompany(companyUuid: string): Observable<CompanyShareAggregateEntity[]> {
    return this._analysisApiAccess.get<CompanyShareAggregateEntity[]>(
      `/company/${companyUuid}/company-share-aggregate`,
      {},
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyShareOwnershipDirectListCache = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipDirectListByCompany,
  );
  public fetchCompanyShareOwnershipDirectListByCompany(
    params: ShareholderParams,
  ): Observable<CompanyShareOwnershipDirectEntity[]> {
    if (params.shareIssuerCompanyIdentifier && params.shareholderCompanyIdentifier) {
      throw new Error('Can only fetch from either companyIdentifier or shareholderCompanyUuid not both');
    }
    if (
      (params.shareIssuerCompanyIdentifier && params.shareholderPersonUuid) ||
      (params.shareholderCompanyIdentifier && params.shareholderPersonUuid)
    ) {
      throw new Error('Can only fetch from either companyIdentifier or person not both');
    }
    const httpParams = params.getShareholderHttpParams();

    return this._analysisApiAccess.get<CompanyShareOwnershipDirectEntity[]>(
      '/company-share-ownership-direct',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  public companyShareOwnershipComposite = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipCorporateGroupComposite,
  );
  public fetchCompanyShareOwnershipCorporateGroupComposite(
    companyIdentifier: string,
    ownershipYear: number,
    companyShareOwnershipTypeIdentifier: CompanyShareOwnershipTypeIdentifier = 'direct_ownership',
    ownershipFilterKey: OwnershipFilterKey = 'corporate_group',
  ): Observable<CompanyShareOwnershipCompositeEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_identifier', companyIdentifier);
    httpParams = httpParams.append('company_share_ownership_type_identifier', companyShareOwnershipTypeIdentifier);
    httpParams = httpParams.append('ownership_filter_key', ownershipFilterKey);
    httpParams = httpParams.append('ownership_year', String(ownershipYear));
    return this._analysisApiAccess.get<CompanyShareOwnershipCompositeEntity[]>(
      'company-share-ownership-composite',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyShareOwnershipUltimateListByShareholderCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipUltimateListByShareholderCompany,
  );
  public fetchCompanyShareOwnershipUltimateListByShareholderCompany(
    shareholderCompanyIdentifier: string,
  ): Observable<CompanyShareOwnershipUltimateEntity[]> {
    // TODO: Change this to a better way, a solution to get all ownerships.
    let httpParams = new HttpParams();
    httpParams = httpParams.append('lookback_years', '10');
    httpParams = httpParams.append('shareholder_company_identifier', shareholderCompanyIdentifier);
    return this._analysisApiAccess.get<CompanyShareOwnershipUltimateEntity[]>(
      '/company-share-ownership-ultimate',
      {
        params: httpParams,
      },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  public companyShareOwnershipUltimateListByShareIssuerCompanyAndYearCache = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipUltimateListByShareIssuerCompanyAndYear,
  );
  public fetchCompanyShareOwnershipUltimateListByShareIssuerCompanyAndYear(
    companyUuid: string,
    year: number,
    limit: number,
    lookbackYears = 1,
  ): Observable<CompanyShareOwnershipUltimateEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('lookback_years', String(lookbackYears));
    httpParams = httpParams.append('year', String(year));
    httpParams = httpParams.append('limit', String(limit));
    httpParams = httpParams.append('share_issuer_company_identifier', companyUuid);
    return this._analysisApiAccess.get<CompanyShareOwnershipUltimateEntity[]>(
      '/company-share-ownership-ultimate',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  public companyEventAggregateListCache = this._cachingService.create(this, this.fetchCompanyEventAggregateList);
  public fetchCompanyEventAggregateList(
    companyUuid: string,
    fromPublishedTimestamp: string,
  ): Observable<CompanyEventAggregate[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('from_published_timestamp', fromPublishedTimestamp);
    return this._applicationApiAccess.get<CompanyEventAggregate[]>(
      `/company/${companyUuid}/company-event-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.events_news, AppFeatureKey.company_risk_score, ...COUNTRY_FEATURES],
    );
  }

  public fetchCompanyCustomerUploadFileBinary(companyCustomerUploadFileUuid: string): Observable<Blob> {
    return this._applicationApi.getBlob(`/company-customer-upload-file/${companyCustomerUploadFileUuid}?binary=true`);
  }

  public deleteCompanyCustomerUploadMetaByUuid(
    companyCustomerUploadMetaUuid: string,
  ): Observable<CompanyCustomerUploadMetaEntity> {
    return this._applicationApiAccess.delete<CompanyCustomerUploadMetaEntity>(
      `/company-customer-upload-meta/${companyCustomerUploadMetaUuid}`,
    );
  }

  public postCompanyCustomerUploadByCustomer(
    customerUuid: string,
    companyCustomerUploadFile: File,
    validFromDate: Date,
    contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ): Observable<HttpEvent<unknown>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('upload_name', companyCustomerUploadFile.name);
    httpParams = httpParams.append('valid_from_date', validFromDate.toISOString().slice(0, 10));
    httpParams = httpParams.append('content_type', contentType);

    const options = {
      params: httpParams,
      reportProgress: true,
    };

    const url = `/app-customer/${customerUuid}/company-customer-upload-meta`;
    return this._applicationApi.postFile(url, companyCustomerUploadFile, options);
  }

  public companyEmploymentsListByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyEmploymentsListByCompany,
  );
  public fetchCompanyEmploymentsListByCompany(companyUuid: string): Observable<CompanyEmploymentEntity[]> {
    return this._analysisApiAccess.get<CompanyEmploymentEntity[]>(`/company/${companyUuid}/company-employment`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public companyCollectionByBatchCache = this._cachingService.create(this, this.fetchCompanyCollectionByBatch);
  public fetchCompanyCollectionByBatch(
    companyBatchUuid: string,
    limit = 100,
    offset = 0,
  ): Observable<CompanyCollectionEntity> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('limit', String(limit));
    httpParams = httpParams.append('offset', String(offset));
    return this._analysisApiAccess.get<CompanyCollectionEntity>(
      `/company-batch/${companyBatchUuid}/company-collection`,
      {
        params: httpParams,
      },
    );
  }

  public companyDescriptionCache = this._cachingService.create(this, this.fetchCompanyDescription);
  public fetchCompanyDescription(
    companyIdentifier: string,
    latestFlag = false,
  ): Observable<CompanyDescriptionEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append(
      'company_description_type_identifiers',
      'brreg_official_purpose,bolagsverket_official_purpose,cvr_official_purpose,asiakastieto_purpose',
    );
    if (latestFlag === true) {
      httpParams = httpParams.append('latest_flag', true);
    }
    return this._analysisApiAccess.get<CompanyDescriptionEntity[]>(
      `/company/${companyIdentifier}/company-description`,
      { params: httpParams },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyRiskScoreOfficialPrioritizedLatestCache = this._cachingService.create(
    this,
    this.fetchCompanyRiskScoreOfficialPrioritizedLatest,
  );
  public fetchCompanyRiskScoreOfficialPrioritizedLatest(
    companyIdentifier: string,
  ): Observable<CompanyRiskScoreOfficialPrioritizedLatestEntity[]> {
    return this._analysisApiAccess.get<CompanyRiskScoreOfficialPrioritizedLatestEntity[]>(
      `/company/${companyIdentifier}/company-risk-score-official-prioritized-latest`,
      {},
      [AppFeatureKey.base, AppFeatureKey.company_risk_score, ...COUNTRY_FEATURES],
    );
  }

  public organizationTypeCache = this._cachingService.create(this, this.fetchOrganizationType);
  public fetchOrganizationType(geoCountryIdentifiers: string[]): Observable<OrganizationTypeEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('geo_country_identifiers', String(geoCountryIdentifiers.join(',')));
    return this._analysisApiAccess.get<OrganizationTypeEntity[]>(`/organization-type`, {
      params: httpParams,
    });
  }

  public companyCompositeCache = this._cachingService.create(this, this.fetchCompanyComposite);
  public fetchCompanyComposite(
    companyIdentifier: string,
    includeHistoricRolesFlag: boolean,
    naceCodeHierarchyLevel: number = null,
  ): Observable<CompanyCompositeEntity> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('person_company_role_current_flag', !includeHistoricRolesFlag);
    if (naceCodeHierarchyLevel) {
      httpParams = httpParams.append('nace_code_hierarchy_level', String(naceCodeHierarchyLevel));
    }
    return this._analysisApiAccess.get<CompanyCompositeEntity>(
      `/company-composite/${companyIdentifier}`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_roles, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyCompositeCollectionCache = this._cachingService.create(this, this.fetchCompanyCompositeCollection);

  public fetchCompanyCompositeCollection(
    geoData: GeoGeometryData,
    naceCodeHierarchyLevel: number | null = null,
    limit = 10,
    offset = 0,
  ): Observable<SimpleCollection<CompanyCompositeEntity>> {
    let params = new HttpParams();
    if ('geoGeometryUuid' in geoData) {
      params = params.append('geo_geometry_uuid', geoData.geoGeometryUuid);
    }
    if ('geoJsonStr' in geoData) {
      params = params.append('geo_json', encodeURIComponent(geoData.geoJsonStr));
    }
    if ('geomType' in geoData) {
      params = params.append('geom.type', geoData.geomType);
      params = params.append('geom.coordinates', geoData.geomCoordinates);
      if ('geomPropertyRadius' in geoData) {
        params = params.append('geom.property.radius', geoData.geomPropertyRadius);
      }
    }
    if (naceCodeHierarchyLevel) {
      params = params.append('nace_code_hierarchy_level', String(naceCodeHierarchyLevel));
    }
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this._analysisApiAccess.get<SimpleCollection<CompanyCompositeEntity>>(
      'company-composite-collection',
      {
        params,
      },
      [AppFeatureKey.base, AppFeatureKey.historical_roles, ...COUNTRY_FEATURES],
    );
  }
}
